import { useState, useEffect } from 'react';
import { ethers } from 'ethers6';
import { executeWithRpcFailover } from '../utils/rpcProvider';
import { config } from '../utils/config';
import { getRelayTokenPrice } from './useRelayPrice';
import { supabase } from '../utils/supabase';
import { fetchTokenBalancesFromExplorer } from '../utils/explorerApi';

// MavrkLinearVesting contract address
const VESTING_ADDRESS = config.ink.vestingContractAddress;

// ERC20 ABI for getting token decimals if not provided by explorer
const ERC20_ABI = [
  "function decimals() view returns (uint8)",
  "function symbol() view returns (string)",
  "function name() view returns (string)"
];

export interface VestedTokenData {
  tokenAddress: string;
  tokenAmount: number; // Human-readable amount
  symbol: string;
  name: string;
  logo_url?: string;
  tokenPriceUSD: number;
  usdValue: number;
  vestingCount: number; // Number of individual vesting schedules for this token
}

export interface UseVestedTokensResult {
  tokens: VestedTokenData[];
  totalTVL: number;
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook to fetch and process all vested tokens from MavrkLinearVesting
 * Queries Ink Explorer API directly for token balances (much more efficient!)
 * Uses Relay API for prices and Supabase for logos
 */
export function useVestedTokens(): UseVestedTokensResult {
  const [tokens, setTokens] = useState<VestedTokenData[]>([]);
  const [totalTVL, setTotalTVL] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    async function fetchVestedTokens() {
      try {
        setIsLoading(true);
        setError(null);

        console.log('[VestedTokens] Fetching token balances from Ink Explorer API...');

        if (!isMounted) return;

        // Step 1: Fetch token balances directly from explorer API
        const explorerTokens = await fetchTokenBalancesFromExplorer(VESTING_ADDRESS);
        
        console.log(`[VestedTokens] Found ${explorerTokens.length} tokens with balances from explorer API`);

        if (!isMounted) return;

        // Step 2: Get token prices from Relay API and logos from Supabase
        const processedTokens: VestedTokenData[] = [];
        
        if (explorerTokens.length > 0) {
          // First, fetch logos from Supabase for known tokens
          const tokenAddresses = explorerTokens.map(t => t.tokenAddress?.toLowerCase()).filter(Boolean) as string[];
          console.log(`[VestedTokens] Querying Supabase for logos for ${tokenAddresses.length} token addresses:`, tokenAddresses);
          
          // Use case-insensitive matching by using OR conditions with ilike (exact match)
          const orConditions = tokenAddresses.map(addr => `contract_address.ilike.${addr}`).join(',');
          const { data: supabaseTokens, error: supabaseError } = await supabase
            .from('tokens')
            .select('contract_address, logo_url, name, symbol')
            .eq('network', 'ink')
            .or(orConditions);
          
          if (supabaseError) {
            console.warn('[VestedTokens] Supabase query error:', supabaseError);
          } else {
            console.log(`[VestedTokens] Found ${supabaseTokens?.length || 0} tokens in Supabase with logos`);
          }
          
          // Create a case-insensitive map
          const supabaseTokenMap = new Map(
            (supabaseTokens || []).map(t => [t.contract_address.toLowerCase(), t])
          );
          
          // Log which tokens have logos
          explorerTokens.forEach(t => {
            const addr = t.tokenAddress?.toLowerCase();
            const supabaseToken = supabaseTokenMap.get(addr || '');
            if (supabaseToken) {
              console.log(`[VestedTokens] Found logo for ${addr}: ${supabaseToken.logo_url}`);
            } else {
              console.warn(`[VestedTokens] No logo found in Supabase for ${addr}`);
            }
          });

          // Process each token from explorer
          const pricePromises = explorerTokens.map(async (explorerToken) => {
            try {
              const tokenAddress = explorerToken.tokenAddress!;
              const balanceBigInt = BigInt(explorerToken.balance || '0');
              
              // Get decimals - use from explorer if available, otherwise fetch from RPC
              let tokenDecimals: number;
              if (explorerToken.decimals !== undefined && explorerToken.decimals !== null) {
                // Convert to number if it's a string, or use directly if it's already a number
                tokenDecimals = typeof explorerToken.decimals === 'string' 
                  ? parseInt(explorerToken.decimals, 10) 
                  : Number(explorerToken.decimals);
                
                // Validate it's a valid number (between 0 and 255 for ERC20)
                if (isNaN(tokenDecimals) || tokenDecimals < 0 || tokenDecimals > 255) {
                  tokenDecimals = await executeWithRpcFailover(async (provider) => {
                    const tokenContract = new ethers.Contract(tokenAddress, ERC20_ABI, provider);
                    const decimalsBigInt = await tokenContract.decimals();
                    return Number(decimalsBigInt);
                  });
                }
              } else {
                // No decimals from explorer, fetch from RPC
                tokenDecimals = await executeWithRpcFailover(async (provider) => {
                  const tokenContract = new ethers.Contract(tokenAddress, ERC20_ABI, provider);
                  const decimalsBigInt = await tokenContract.decimals();
                  return Number(decimalsBigInt);
                });
              }

              // Get token metadata - prefer Supabase, then explorer, then defaults
              const supabaseToken = supabaseTokenMap.get(tokenAddress.toLowerCase());
              const symbol = explorerToken.symbol || supabaseToken?.symbol || '???';
              const name = explorerToken.name || supabaseToken?.name || 'Unknown Token';
              // Use logo from Supabase tokens table
              const logo_url = supabaseToken?.logo_url || explorerToken.logoUri || undefined;

              // Get token price from Relay Protocol API
              const tokenPriceUSD = await getRelayTokenPrice(tokenAddress);

              if (!tokenPriceUSD || tokenPriceUSD <= 0) {
                console.warn(`[VestedTokens] No price available for ${symbol} (${tokenAddress})`);
                return null;
              }

              // Format token amount using token decimals
              const tokenAmountHuman = Number(ethers.formatUnits(balanceBigInt, tokenDecimals));
              const usdValue = tokenAmountHuman * tokenPriceUSD;

              console.log(`[VestedTokens] ${symbol}: ${tokenAmountHuman.toFixed(2)} tokens @ $${tokenPriceUSD.toFixed(6)} = $${usdValue.toFixed(2)}`);

              return {
                tokenAddress,
                tokenAmount: tokenAmountHuman,
                symbol,
                name,
                logo_url, // From Supabase tokens table!
                tokenPriceUSD,
                usdValue,
                vestingCount: 1 // We don't track individual vesting count with this approach
              };
            } catch (err) {
              console.warn(`[VestedTokens] Failed to process token ${explorerToken.tokenAddress}:`, err);
              return null;
            }
          });

          // Wait for all price calculations to complete
          const results = await Promise.all(pricePromises);
          const validResults = results.filter((result): result is NonNullable<typeof result> => result !== null);
          processedTokens.push(...validResults);
        }

        if (!isMounted) return;

        // Sort by USD value (highest first)
        processedTokens.sort((a, b) => b.usdValue - a.usdValue);

        // Calculate total TVL
        const tvl = processedTokens.reduce((sum, token) => sum + token.usdValue, 0);

        setTokens(processedTokens);
        setTotalTVL(tvl);
        setIsLoading(false);

        console.log('[VestedTokens] Total TVL:', tvl.toFixed(2), 'USD');
      } catch (err) {
        console.error('[VestedTokens] Error fetching vested tokens:', err);
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Unknown error');
          setIsLoading(false);
        }
      }
    }

    fetchVestedTokens();

    return () => {
      isMounted = false;
    };
  }, []);

  return { tokens, totalTVL, isLoading, error };
}
